import React from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import {
  Avatar, Switch, Typography, TextField, Button, CircularProgress,
} from '@material-ui/core';
import { Mutation } from '@apollo/client/react/components';
import { withStyles } from '@material-ui/styles';
import { useApolloClient, useQuery } from '@apollo/client';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useTheme from '@material-ui/styles/useTheme';
import { Scrollbars } from 'react-custom-scrollbars-2';
import searchUsersV2Query from '../../../graphql/reports/search-users-v2';
import useDebounce from '../../../utils/useDebounce';
import { stringToColor } from '../../../utils/random-color';
import UserActionMenu from '../../../components/common/UserActionMenu';
import updateRoleMutation from '../../../graphql/user/mutation/update-role';
import PushNotification from '../../../components/common/PushNotification';
import CreateUserDialog from '../../../components/common/CreateUserDialog';

const R = require('ramda');

const styles = (theme) => ({
  root: {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'visible',
    padding: 15,
    height: 'calc(100vh - 100px)',
  },
  table: {
    // minWidth: 600,

  },
  tableHead: {
    fontWeight: 700,
    color: '#222a42b3',
    fontSize: '0.875rem',
  },
  tableBody: {
    fontWeight: 400,
    color: '#222a42b3',
    fontSize: '0.875rem',
  },
  dense: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  searchbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',

  },
  message: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 32,
  },

});

const AllUsers = ({ classes }) => {
  const client = useApolloClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [moreToLoad, setMoreToLoad] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [state, setState] = React.useState({});
  const [newRoles, setRoles] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [filterValues, setFilterValues] = React.useState({
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    offset: 0,
    limit: 50,
  });
  const [total, setTotal] = React.useState(0);

  // Debounce the four search fields (300ms): one query per pause instead of
  // one query per keystroke. offset/limit intentionally NOT debounced.
  const debouncedFirstName = useDebounce(filterValues.firstName);
  const debouncedLastName = useDebounce(filterValues.lastName);
  const debouncedEmail = useDebounce(filterValues.email);
  const debouncedPhone = useDebounce(filterValues.phone);
  const debouncedFilters = {
    firstName: debouncedFirstName,
    lastName: debouncedLastName,
    email: debouncedEmail,
    phone: debouncedPhone,
  };

  React.useEffect(() => {
    const subscription = client.watchQuery({
      query: searchUsersV2Query,
      variables: { ...debouncedFilters, offset: 0, limit: filterValues.limit },
    })
      .subscribe(({ data }) => {
        if (data && data.searchUsersV2) {
          setUsers(data.searchUsersV2.users);
          setTotal(data.searchUsersV2.total);
          data.searchUsersV2.users.length < filterValues.limit
            ? setMoreToLoad(false)
            : setMoreToLoad(true);
        }
        setLoading(false);
      }, (error) => {
        console.log('Error performing searchUsersV2', error);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [debouncedFirstName, debouncedLastName, debouncedEmail, debouncedPhone, filterValues.limit]);

  React.useEffect(() => {
    if (filterValues.offset <= 0) return;
    const subscription = client.watchQuery({
      query: searchUsersV2Query,
      variables: {
        ...debouncedFilters,
        offset: filterValues.offset,
        limit: filterValues.limit,
      },
    })
      .subscribe(({ data }) => {
        if (data && data.searchUsersV2) {
          setUsers((prevUsers) => [...prevUsers, ...data.searchUsersV2.users]);
          data.searchUsersV2.users.length < filterValues.limit
            ? setMoreToLoad(false)
            : setMoreToLoad(true);
        }
        setLoading(false);
      }, (error) => {
        console.log(error);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [filterValues.offset]);

  const handleFilterChange = (name) => (event) => {
    setFilterValues({ ...filterValues, offset: 0, [name]: event.target.value });
  };

  const handleChange = (user, mutation, _id) => {
    const { roles } = user;
    const newRoles = ['user'];
    roles.includes('admin') && newRoles.push('admin');
    !roles.includes('trainer') && newRoles.push('trainer');
    setRoles(newRoles);
    mutation({ variables: { _id, roles: newRoles } });
  };

  const handleRefetch = async () => {
    /* await setFilterValues({ ...filterValues, limit: 49 }); */
    setFilterValues({ ...filterValues, limit: 50 });
  };

  return (
    <Paper className={classes.root}>
      <Typography variant="h5" style={{ fontWeight: 100, marginLeft: 8 }}>
        All Users
      </Typography>
      <div className={classes.searchbar}>

        <TextField
          id="outlined-dense"
          label="First Name"
          className={classes.dense}
          value={filterValues.firstName}
          style={{ width: isMobile ? '100%' : 175 }}
          margin="dense"
          variant="outlined"
          onChange={handleFilterChange('firstName')}
        />
        <TextField
          id="outlined-dense"
          label="Last Name"
          value={filterValues.lastName}
          style={{ width: isMobile ? '100%' : 175 }}
          className={classes.dense}
          margin="dense"
          variant="outlined"
          onChange={handleFilterChange('lastName')}
        />
        <TextField
          id="outlined-dense"
          label="Email"
          value={filterValues.email}
          style={{ width: isMobile ? '100%' : 175 }}
          className={classes.dense}
          margin="dense"
          variant="outlined"
          onChange={handleFilterChange('email')}
        />
        <TextField
          id="outlined-dense"
          label="Phone"
          value={filterValues.phone}
          style={{ width: isMobile ? '100%' : 175 }}
          className={classes.dense}
          margin="dense"
          variant="outlined"
          onChange={handleFilterChange('phone')}
        />
        <CreateUserDialog refetch={handleRefetch} />
      </div>
      <Scrollbars
        autoHeight
        autoHeightMin={300}
        autoHeightMax="calc(59vh)"
        autoHide
        style={{ width: '100%' }}
      >
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead}>
                <PushNotification icon />
              </TableCell>
              <TableCell className={classes.tableHead}>Name</TableCell>
              <TableCell className={classes.tableHead}>Email</TableCell>
              <TableCell className={classes.tableHead}>Phone</TableCell>
              <TableCell className={classes.tableHead}>Company</TableCell>
              <TableCell className={classes.tableHead}>Trainer</TableCell>
              <TableCell className={classes.tableHead}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users && users.length > 0 && Array.from(users).map((user) => {
              const company = R.pathOr('', ['name'])(user.company);
              const { _id } = user;
              return (

                <TableRow key={user._id}>
                  <TableCell component="th" scope="row">
                    {user.image
                      ? <Avatar alt={user.firstName} src={user.image} />
                      : (
                        <Avatar
                          style={{ backgroundColor: stringToColor(user.firstName ? user.firstName : 'empty') }}
                        > {user.firstName ? user.firstName[0] : 'N'}
                        </Avatar>
                      )}
                  </TableCell>
                  <TableCell
                    className={classes.tableBody}
                  >{user.firstName} {user.lastName}
                  </TableCell>
                  <TableCell className={classes.tableBody}>{user.email}</TableCell>
                  <TableCell className={classes.tableBody}>{user.phone}</TableCell>
                  <TableCell
                    className={classes.tableBody}
                  >{company}
                  </TableCell>
                  <TableCell className={classes.tableBody}>
                    <Mutation mutation={updateRoleMutation}>
                      {(updateRoleMutation, { loading, error }) => (
                        <Switch
                          checked={state._id}
                          name={_id}
                          defaultChecked={user.roles.includes('trainer')}
                          onChange={() => {
                            handleChange(user, updateRoleMutation, user._id);
                          }}
                          color="primary"
                          inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                      )}

                    </Mutation>
                  </TableCell>
                  <TableCell>
                    <UserActionMenu user={user} refetch={handleRefetch} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {
          (users.length === 0 && loading) && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </div>
          )
        }

        {
          (users.length === 0 && !loading) && (
            <div className={classes.message}>
              <Typography variant="h5">
                There are no active users for this company.
              </Typography>
            </div>
          )
        }
      </Scrollbars>
      <div style={{ width: '100%', textAlign: 'center' }}>
        <Button
          disabled={!moreToLoad}
          onClick={() => {
            setFilterValues({ ...filterValues, offset: filterValues.offset + 50 });
          }}
        >{moreToLoad ? 'Show More' : `Showing All Users${total ? ` (${total})` : ''}`}
        </Button>
      </div>
    </Paper>
  );
};

export default withStyles(styles)(AllUsers);
